import { DOCUMENT } from '@angular/common';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { markLanguageUserChosen } from '../../../core/i18n/language-storage';
import { AuthService } from '../../../core/services/auth.service';
import { Translation } from '../../../core/i18n/translation.service';
import { TokenStorageService } from '../../../core/storage/token-storage.service';
import { AppLanguage } from '../../enums/app-language';
import type { SupportedLanguage } from '../../../core/i18n/language-storage';

@Component({
  selector: 'app-language-switcher',
  standalone: true,
  imports: [],
  templateUrl: './language-switcher.html',
  styleUrl: './language-switcher.scss',
})
export class LanguageSwitcher {
  private readonly destroyRef = inject(DestroyRef);
  readonly translation = inject(Translation);
  private readonly auth = inject(AuthService);
  private readonly tokens = inject(TokenStorageService);
  private readonly document = inject(DOCUMENT);

  protected readonly currentLanguage = signal<SupportedLanguage>(this.translation.getCurrentLang());
  protected readonly menuOpen = signal(false);
  protected readonly switching = signal(false);
  protected readonly languages: readonly { code: SupportedLanguage; label: string }[] = [
    { code: 'en-US', label: 'EN' },
    { code: 'ar-SA', label: 'AR' },
  ];
  protected readonly currentLanguageLabel = computed(
    () => this.languages.find((language) => language.code === this.currentLanguage())?.label ?? 'EN',
  );
  protected readonly languageAriaKey = computed(() => 'COMMON.LANGUAGE.TOGGLE');

  constructor() {
    this.translation.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.currentLanguage.set(this.translation.getCurrentLang());
      });
  }

  toggleMenu(): void {
    if (this.switching()) return;
    this.menuOpen.update((open) => !open);
  }

  switchLanguage(language: SupportedLanguage): void {
    if (this.switching()) return;
    this.menuOpen.set(false);
    if (language === this.currentLanguage()) return;

    const next = this.translation.setLanguage(language, { reloadPage: false });
    markLanguageUserChosen();
    const appLang = this.translation.toAppLanguage(next);
    if (
      this.tokens.hasValidTokens() &&
      Object.values(AppLanguage).includes(appLang)
    ) {
      this.switching.set(true);
      this.auth
        .updateUserLanguage(appLang)
        .pipe(finalize(() => this.reloadCurrentPage()))
        .subscribe({ error: () => undefined });
      return;
    }

    this.reloadCurrentPage();
  }

  private reloadCurrentPage(): void {
    this.document.defaultView?.location.reload();
  }
}
